// src/stateManagers/roleAccess/useRoleAccess.tsx
import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { create } from 'zustand';
import LoadingScreen from '../../components/LoadingScreen';
import apiClient from '../services/axiosInterceptor';
import { useAuthStore } from '../store/authStore';

type Access = Record<string, Record<string, boolean>>;

// These roles get everything, no API call
const FULL_ROLES = ['admin', 'superadmin'];

interface Store {
  access: Access | null;
  userId: string | null;
  load: (id: string) => Promise<void>;
  reset: () => void;
}

const useStore = create<Store>((set, get) => ({
  access: null,
  userId: null,

  load: async (id) => {
    if (get().userId === id) return; // already loaded / loading for this user
    set({ userId: id, access: null });
    try {
      const res = await apiClient.get('my-role-access');

      if (get().userId !== id) return; // user changed while request was in flight
      set({ access: res.data.access ?? {} });
    } catch {
      // no access, and no retry loop
      if (get().userId === id) set({ access: {} });
    }
  },

  reset: () => set({ access: null, userId: null }),
}));

export const useRoleAccess = () => {
  const user = useAuthStore((s) => s.user);
  const access = useStore((s) => s.access);
  const forUser = useStore((s) => s.userId);
  const load = useStore((s) => s.load);
  const reset = useStore((s) => s.reset);

  const isFull = !!user && FULL_ROLES.includes(user.role);

  useEffect(() => {
    if (!user) {
      reset(); // logged out: clear so the next login refetches
      return;
    }
    if (!isFull) load(user.id);
  }, [user?.id, isFull]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = isFull || (access !== null && forUser === user?.id);

  // can('students')        -> has any permission in that module
  // can('students', 'add') -> has that exact permission
  const can = (module: string, action?: string) => {
    if (isFull) return true;
    const m = access?.[module];
    if (!m) return false;
    return action ? m[action] === true : Object.values(m).some((v) => v === true);
  };

  // rule: 'public' | 'admin' | 'module' | 'module:action'
  //   'students'      -> needs students.view
  //   'students:add'  -> needs students.add
  const allowed = (rule: string) => {
    if (rule === 'public') return true;
    if (isFull) return true;
    if (rule === 'admin') return false;
    const [module, action = 'view'] = rule.split(':');
    return can(module, action);
  };

  return { ready, isFull, can, allowed };
};

// Per-module shortcut for use deep inside components:
// const { canView, canAdd, canEdit, canDelete, can } = useModuleAccess('students');
export const useModuleAccess = (module: string) => {
  const { can, ready } = useRoleAccess();
  return {
    ready,
    canView: can(module, 'view'),
    canAdd: can(module, 'add'),
    canEdit: can(module, 'edit'),
    canDelete: can(module, 'delete'),
    can: (action: string) => can(module, action),
  };
};

// Inline wrapper: <Can module="students" action="delete">...</Can>
export const Can: React.FC<{
  module: string;
  action: string;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}> = ({ module, action, fallback = null, children }) => {
  const { can } = useRoleAccess();
  return can(module, action) ? <>{children}</> : <>{fallback}</>;
};

// Route guard: <Guard rule={ruleOf(pathname)}>...</Guard>
// (App.tsx uses it once, around all routes.)
export const Guard: React.FC<{ rule: string; children: React.ReactNode }> = ({ rule, children }) => {
  const { ready, allowed } = useRoleAccess();
  if (!ready) return <LoadingScreen />;
  return allowed(rule) ? <>{children}</> : <Navigate to="/" replace />;
};